import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DisplacementBump — who is gaining and losing ground at this account.
 *
 * A trajectory chart answers "how much"; this answers "compared to whom",
 * which is the question a rep is actually carrying. Rank per fiscal year, one
 * line per vendor: a single line climbing through four flat ones is a story
 * that goes into a QBR without editing. Measured on real accounts before this
 * was drawn — Motorola falls 1 -> 6 at Caltrans, HP climbs 5 -> 2 at CDCR — so
 * the movement this exists to show is genuinely there.
 *
 * Rank, not spend, on the y axis. Spend would put one enormous vendor at the
 * top and compress everyone else into the floor, which is the failure the
 * treemap already covers. Rank is the comparison.
 *
 * Built to the board's design language: no box, marks are `.ts-mark-target`
 * so they are keyboard-reachable, figures take `.ts-figure`, and the caveat
 * lives in `.ts-foot` rather than in a tooltip nobody opens.
 *
 * Presentational: data via props, no fetching, colours from CSS variables.
 */
import { useMemo, useRef, useState } from 'react';
const defaultFormatValue = (v) => {
    if (v >= 1000000000)
        return `$${(v / 1000000000).toFixed(1)}B`;
    if (v >= 1000000)
        return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000)
        return `$${(v / 1000).toFixed(0)}K`;
    return `$${Math.round(v)}`;
};
/**
 * How loudly a line is drawn, from how far it MOVED.
 *
 * The chart is called "gaining and losing" and used to draw a vendor that sat
 * at rank 3 for five years exactly like one that fell 1 -> 6: same grey, same
 * width, same opacity. With ten vendors over five years that is spaghetti, and
 * the movement the chart exists to show is the first thing lost in it.
 *
 * Continuous rather than a threshold, so there is no cliff where a vendor
 * moving 2 looks categorically different from one moving 1 — and no arbitrary
 * "top N movers" that changes meaning as the account changes.
 */
const emphasis = (delta) => {
    const m = Math.min(Math.abs(delta), 4) / 4;
    return { opacity: 0.16 + m * 0.54, width: 1 + m * 1.3 };
};
const W = 560;
const H = 300;
// 30, not 12: the y axis is RANK and nothing said so. Without "#1" at the top
// the reader cannot tell which direction is good, and a bump chart is entirely
// about direction.
const PAD_L = 30;
const PAD_R = 126; // room for the name labels at the right edge
// A label is 11px text in a ~15px box; 15 viewBox units is the smallest gap
// that keeps two boxes apart. Ties at the right edge are common (a vendor that
// left the chart keeps the rank it held when it left), so without this two
// names are drawn at exactly the same y and neither can be read.
const LABEL_GAP = 15;
const PAD_T = 22;
const PAD_B = 30;
export const DisplacementBump = ({ points, ourVendorMatch = 'palo alto', ourPinned = false, scopeNote, fill = false, height = 300, onPick, formatValue = defaultFormatValue, }) => {
    /*
     * THE MARKS WORK AND CANNOT BE HIT.
     *
     * Ryan: "Each one of these dots needs to be able to drill down to the PO's
     * and data underneath it." They already could — clicking one opens that
     * vendor's lines for that fiscal year and reconciles exactly (measured:
     * Oracle FY23, chart $172K, list "21 rows · $172K · 1 POs"). The problem is
     * that a mark renders 7px across, so finding one is a game of pixel-hunting,
     * and nothing on the chart says the dots are live at all.
     *
     * Same answer as the ceiling gauge and the site map: the pointer talks to
     * the PLOT, not to a mark. One overlay resolves the nearest mark, which
     * makes every dot reachable from ~3x its own radius away, and a real tooltip
     * says what is under the pointer and that it opens.
     */
    const svgRef = useRef(null);
    const [hit, setHit] = useState(null);
    const hitKey = hit ? `${hit.m.vendor}|${hit.m.year}` : null;
    const { years, lines, worst } = useMemo(() => {
        const ys = [...new Set((points || []).map((p) => p.year))].sort((a, b) => a - b);
        if (ys.length < 2)
            return { years: ys, lines: [], worst: 0 };
        // Rank within each year independently — that IS the chart.
        const rankOf = new Map();
        let deepest = 0;
        for (const y of ys) {
            const inYear = (points || [])
                .filter((p) => p.year === y && p.spend > 0)
                .sort((a, b) => b.spend - a.spend);
            inYear.forEach((p, i) => {
                if (!rankOf.has(p.vendor))
                    rankOf.set(p.vendor, new Map());
                rankOf.get(p.vendor).set(y, { rank: i + 1, spend: p.spend });
                deepest = Math.max(deepest, i + 1);
            });
        }
        const built = [...rankOf.entries()].map(([vendor, byYear]) => {
            const marks = ys
                .filter((y) => byYear.has(y))
                .map((y) => ({
                vendor,
                year: y,
                rank: byYear.get(y).rank,
                spend: byYear.get(y).spend,
                ours: vendor.toLowerCase().includes(ourVendorMatch),
            }));
            const first = marks[0];
            const last = marks[marks.length - 1];
            return {
                vendor,
                ours: vendor.toLowerCase().includes(ourVendorMatch),
                marks,
                // Negative delta = climbed (rank 5 -> 2). Stated in the label as
                // direction words, because "▲ 3" is ambiguous when up is down.
                delta: first && last ? first.rank - last.rank : 0,
                lastRank: last ? last.rank : Infinity,
            };
        }).sort((a, b) => a.lastRank - b.lastRank);
        return { years: ys, lines: built, worst: deepest };
    }, [points, ourVendorMatch]);
    if (years.length < 2 || lines.length === 0) {
        return (_jsx("div", { className: `chs__empty${fill ? ' chs__empty--fill' : ''}`, style: fill ? undefined : { '--h': `${height}px` }, children: "Not enough years of classified spend to compare." }));
    }
    const x = (y) => PAD_L + ((y - years[0]) / Math.max(years.length - 1, 1)) * (W - PAD_L - PAD_R);
    const yPos = (rank) => PAD_T + ((rank - 1) / Math.max(worst - 1, 1)) * (H - PAD_T - PAD_B);
    /** Pointer -> the nearest mark, in the SVG's own units. */
    const nearest = (clientX, clientY) => {
        const el = svgRef.current;
        if (!el)
            return null;
        // getScreenCTM accounts for the viewBox AND the letterboxing that
        // preserveAspectRatio introduces; a bounding-rect ratio would be off by
        // the margins whenever the cell is not the drawing's own proportion.
        const ctm = el.getScreenCTM();
        if (!ctm)
            return null;
        const pt = el.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const u = pt.matrixTransform(ctm.inverse());
        let best = null;
        let bestD = 20 * 20;
        for (const line of lines) {
            for (const m of line.marks) {
                const dx = x(m.year) - u.x;
                const dy = yPos(m.rank) - u.y;
                const d = dx * dx + dy * dy;
                if (d <= bestD) {
                    bestD = d;
                    best = { m, ours: line.ours };
                }
            }
        }
        return best;
    };
    /*
     * Where each NAME sits, which is not simply where its line ends. A vendor
     * absent from the final year keeps the rank it held when it left, so two
     * names routinely resolve to the same y — on CDT three pairs did, and each
     * pair was drawn one on top of the other. Walk them in order, push any that
     * are too close apart, and if the stack runs past the plot pull it back up
     * and resolve upward instead. The dot still marks the true rank; only the
     * text moves.
     */
    const labelY = (() => {
        const items = lines
            .map((l) => ({ vendor: l.vendor, y: yPos(l.marks[l.marks.length - 1].rank) + 3.5 }))
            .sort((a, b) => a.y - b.y);
        for (let i = 1; i < items.length; i += 1) {
            if (items[i].y - items[i - 1].y < LABEL_GAP)
                items[i].y = items[i - 1].y + LABEL_GAP;
        }
        const bottom = H - PAD_B + 4;
        const spill = items.length ? items[items.length - 1].y - bottom : 0;
        if (spill > 0) {
            for (const it of items)
                it.y -= spill;
            for (let i = items.length - 2; i >= 0; i -= 1) {
                if (items[i + 1].y - items[i].y < LABEL_GAP)
                    items[i].y = items[i + 1].y - LABEL_GAP;
            }
        }
        return new Map(items.map((it) => [it.vendor, it.y]));
    })();
    return (_jsxs("div", { className: `dbp${fill ? ' chfill' : ''}`, children: [_jsxs("svg", { ref: svgRef, viewBox: `0 0 ${W} ${H}`, width: "100%", height: fill ? '100%' : height, preserveAspectRatio: "xMidYMid meet", role: "img", "aria-label": `Vendor rank by fiscal year, FY${String(years[0]).slice(-2)} to FY${String(years[years.length - 1]).slice(-2)}`, className: `chsvg${fill ? ' chsvg--fill' : ''}`, children: [_jsx("text", { x: PAD_L - 8, y: PAD_T + 4, textAnchor: "end", fontSize: 10, fill: "var(--text-muted, #9ca3af)", children: "#1" }), worst > 1 && (_jsx("text", { x: PAD_L - 8, y: H - PAD_B + 4, textAnchor: "end", fontSize: 10, fill: "var(--text-muted, #9ca3af)", children: `#${worst}` })), years.map((y) => (_jsxs("text", { x: x(y), y: H - 10, textAnchor: "middle", fontSize: 10, fill: "var(--text-muted, #9ca3af)", children: ["FY", String(y).slice(-2)] }, y))), lines.map((line) => {
                        const d = line.marks.map((m) => `${x(m.year).toFixed(1)},${yPos(m.rank).toFixed(1)}`).join(' ');
                        return (_jsxs("g", { children: [_jsx("polyline", { points: d, fill: "none", stroke: line.ours ? 'var(--accent, #3b82f6)' : 'var(--text-muted, #9ca3af)', strokeOpacity: line.ours ? 1 : emphasis(line.delta).opacity, strokeWidth: line.ours ? 2.5 : emphasis(line.delta).width, strokeLinejoin: "round", strokeLinecap: "round", vectorEffect: "non-scaling-stroke" }), line.marks.map((m) => (_jsx("circle", { 
                                    /* Pointer events live on the overlay below. The mark stays
                                       FOCUSABLE, so the keyboard path is unchanged. */
                                    className: `dbp-dot${onPick ? ' ts-mark-target' : ''}${hitKey === `${line.vendor}|${m.year}` ? ' dbp-dot--on' : ''}`, cx: x(m.year), cy: yPos(m.rank), r: line.ours ? 4.5 : 3, fill: line.ours ? 'var(--accent, #3b82f6)' : 'var(--text-muted, #9ca3af)', fillOpacity: line.ours ? 1 : emphasis(line.delta).opacity, tabIndex: onPick ? 0 : undefined, role: onPick ? 'button' : undefined, onFocus: () => setHit({ m, ours: line.ours }), onBlur: () => setHit((h) => (h?.m === m ? null : h)), onKeyDown: onPick ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onPick(m);
                                        }
                                    } : undefined, "aria-label": `${m.vendor}, FY${String(m.year).slice(-2)}, rank ${m.rank}, ${formatValue(m.spend)}`, children: _jsx("title", { children: `${m.vendor} · FY${String(m.year).slice(-2)} · #${m.rank} · ${formatValue(m.spend)}` }) }, m.year))), (() => {
                                    const last = line.marks[line.marks.length - 1];
                                    if (last.year === years[years.length - 1])
                                        return null;
                                    const ly = labelY.get(line.vendor) ?? yPos(last.rank) + 3.5;
                                    return (_jsx("polyline", { points: `${x(last.year).toFixed(1)},${yPos(last.rank).toFixed(1)} ${(x(years[years.length - 1]) + 6).toFixed(1)},${(ly - 3.5).toFixed(1)}`, fill: "none", stroke: line.ours ? 'var(--accent, #3b82f6)' : 'var(--text-muted, #9ca3af)', strokeOpacity: line.ours ? 0.5 : 0.25, strokeWidth: 1, strokeDasharray: "2 3", vectorEffect: "non-scaling-stroke" }));
                                })(), _jsxs("text", { x: x(years[years.length - 1]) + 10, y: labelY.get(line.vendor) ?? yPos(line.marks[line.marks.length - 1].rank) + 3.5, fontSize: 11, fontWeight: line.ours ? 600 : (Math.abs(line.delta) >= 2 ? 600 : 400), fill: line.ours ? 'var(--accent, #3b82f6)' : 'var(--text-muted, #6b7280)', fillOpacity: line.ours ? 1 : 0.45 + Math.min(Math.abs(line.delta), 4) / 4 * 0.55, children: [(() => {
                                            const cap = line.delta !== 0 ? 13 : 16;
                                            return line.vendor.length > cap ? `${line.vendor.slice(0, cap - 1)}…` : line.vendor;
                                        })(), line.delta !== 0 && (_jsx("tspan", { fill: "var(--text-muted, #9ca3af)", fontWeight: 400, children: line.delta > 0 ? ` ▲${line.delta}` : ` ▼${-line.delta}` })), _jsxs("title", { children: [`${line.vendor} · #${line.marks[line.marks.length - 1].rank} in FY${String(line.marks[line.marks.length - 1].year).slice(-2)}`, line.delta !== 0 ? ` · ${line.delta > 0 ? 'up' : 'down'} ${Math.abs(line.delta)} since FY${String(line.marks[0].year).slice(-2)}` : ''] })] })] }, line.vendor));
                    }), hit && (_jsx("circle", { className: "dbp-dot dbp-ring", cx: x(hit.m.year), cy: yPos(hit.m.rank), r: (hit.ours ? 4.5 : 3) + 4, fill: "none", stroke: hit.ours ? 'var(--accent, #3b82f6)' : 'var(--text-muted, #6b7280)', strokeWidth: 1.5, vectorEffect: "non-scaling-stroke" })), _jsx("rect", { className: `dbp-hit${onPick ? ' chpick' : ''}`, x: 0, y: 0, width: W, height: H, fill: "transparent", onPointerMove: (e) => setHit(nearest(e.clientX, e.clientY)), onPointerLeave: () => setHit(null), onClick: onPick ? (e) => {
                            const n = nearest(e.clientX, e.clientY);
                            if (n)
                                onPick(n.m);
                        } : undefined })] }), hit && (_jsxs("div", { className: `dbp-tip${x(hit.m.year) > W / 2 ? ' dbp-tip--left' : ''}${yPos(hit.m.rank) < H / 2 ? ' dbp-tip--low' : ''}`, 
                /* Only the horizontal anchor is data; the vertical side is chosen so
                   the box never covers the mark being read. */
                style: { '--tip-x': `${(x(hit.m.year) / W) * 100}%` }, children: [_jsx("span", { className: "dbp-tip__v", children: formatValue(hit.m.spend) }), _jsx("span", { className: "dbp-tip__n", children: hit.m.vendor }), _jsx("span", { className: "dbp-tip__m", children: `FY${String(hit.m.year).slice(-2)} · rank #${hit.m.rank}` }), onPick && _jsx("span", { className: "dbp-tip__cta", children: "Click for the purchase orders" })] })), _jsxs("p", { className: "ts-foot", children: ["Rank by ", scopeNote || 'classified technology spend', " \u00B7 California state fiscal year", ourPinned && ' · ours shown though outside the top ranks'] })] }));
};
export default DisplacementBump;
